import { logger } from '../../utils/logger.js'
import maplibregl, { addProtocol, removeProtocol } from 'maplibre-gl'
import { Capacitor } from '@capacitor/core'
import { fromUrl, type GeoTIFF, type GeoTIFFImage } from 'geotiff'
import LandCoverWorker from './land-cover-worker.ts?worker'
import { DEFAULT_ENABLED_CLASSES, LAND_COVER_CLASSES } from './land-cover-types.js'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'

const log = logger.create('terrain')

const PROTOCOL = 'landcover'

function isAborted(signal: AbortSignal): boolean {
  return signal.aborted
}
const SOURCE_ID = 'land-cover-source'
const LAYER_ID = 'land-cover-layer'
const TILE_SIZE = 256
const WORKER_POOL_SIZE = 4
const DEFAULT_OPACITY = 0.7
const MAX_COG_CACHE = 16
const MIN_ZOOM = 6
const MAX_ZOOM = 14

const COG_ORIGIN = Capacitor.isNativePlatform() ? 'https://taselith.com' : ''
const COG_BASE_URL = `${COG_ORIGIN}/worldcover`

interface CogCacheEntry {
  tiff: GeoTIFF
  images: Map<number, GeoTIFFImage>
  bbox: [number, number, number, number] // [west, south, east, north] from image 0
}

class LandCoverService {
  private workers: Worker[] = []
  private workerIndex = 0
  private map: maplibregl.Map | null = null
  private _enabled = false
  private _opacity = DEFAULT_OPACITY
  private _enabledClasses: Set<number> = new Set(DEFAULT_ENABLED_CLASSES)
  private pendingRequests = new Map<string, AbortController>()
  private nextRequestId = 0
  private cogCache = new Map<string, CogCacheEntry>()
  private cogLoading = new Map<string, Promise<CogCacheEntry>>()
  private filterVersion = 0

  get enabled(): boolean {
    return this._enabled
  }
  get opacity(): number {
    return this._opacity
  }
  get enabledClasses(): Set<number> {
    return this._enabledClasses
  }

  private initWorkers(): void {
    if (this.workers.length > 0) {
      return
    }
    for (let i = 0; i < WORKER_POOL_SIZE; i++) {
      this.workers.push(new LandCoverWorker())
    }
  }

  private getWorker(): Worker {
    const worker = this.workers[this.workerIndex]
    this.workerIndex = (this.workerIndex + 1) % this.workers.length
    return worker
  }

  private terminateWorkers(): void {
    this.workers.forEach(w => w.terminate())
    this.workers = []
    this.workerIndex = 0
  }

  register(map: maplibregl.Map): void {
    this.map = map
    addProtocol(PROTOCOL, (params, abortController) => {
      return this.handleTileRequest(params, abortController)
    })
  }

  unregister(): void {
    this.disable()
    try {
      removeProtocol(PROTOCOL)
    } catch (_) {
      /* may not be registered */
    }
    this.terminateWorkers()
    this.cogCache.clear()
    this.cogLoading.clear()
    this.map = null
  }

  enable(): void {
    if (!this.map || this._enabled) {
      return
    }
    this.initWorkers()
    this._enabled = true

    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'raster',
        tiles: [`${PROTOCOL}://{z}/{x}/{y}?f=${this.filterVersion}`],
        tileSize: TILE_SIZE,
        minzoom: MIN_ZOOM,
        maxzoom: MAX_ZOOM,
        attribution: 'Land Cover: ESA WorldCover 2021',
      })
    }

    if (!this.map.getLayer(LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: LAYER_ID,
          type: 'raster',
          source: SOURCE_ID,
          minzoom: MIN_ZOOM,
          paint: {
            'raster-opacity': this._opacity,
            'raster-fade-duration': 150,
          },
        },
        'land-cover'
      )
    }
  }

  disable(): void {
    if (!this.map) {
      return
    }
    this._enabled = false

    for (const [, controller] of this.pendingRequests) {
      controller.abort()
    }
    this.pendingRequests.clear()

    if (this.map.getLayer(LAYER_ID)) {
      this.map.removeLayer(LAYER_ID)
    }
    if (this.map.getSource(SOURCE_ID)) {
      this.map.removeSource(SOURCE_ID)
    }

    this.terminateWorkers()
  }

  toggle(): void {
    if (this._enabled) {
      this.disable()
    } else {
      this.enable()
    }
  }

  reAdd(): void {
    if (!this._enabled || !this.map) {
      return
    }
    this.initWorkers()
    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'raster',
        tiles: [`${PROTOCOL}://{z}/{x}/{y}?f=${this.filterVersion}`],
        tileSize: TILE_SIZE,
        minzoom: MIN_ZOOM,
        maxzoom: MAX_ZOOM,
        attribution: 'Land Cover: ESA WorldCover 2021',
      })
    }
    if (!this.map.getLayer(LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: LAYER_ID,
          type: 'raster',
          source: SOURCE_ID,
          minzoom: MIN_ZOOM,
          paint: {
            'raster-opacity': this._opacity,
            'raster-fade-duration': 150,
          },
        },
        'land-cover'
      )
    }
  }

  setOpacity(opacity: number): void {
    this._opacity = Math.max(0, Math.min(1, opacity))
    if (this.map?.getLayer(LAYER_ID)) {
      this.map.setPaintProperty(LAYER_ID, 'raster-opacity', this._opacity)
    }
  }

  setEnabledClasses(classes: Set<number>): void {
    this._enabledClasses = new Set(classes)
    this.refreshTiles()
  }

  toggleClass(value: number): void {
    if (this._enabledClasses.has(value)) {
      this._enabledClasses.delete(value)
    } else {
      this._enabledClasses.add(value)
    }
    this.refreshTiles()
  }

  enableAllClasses(): void {
    this._enabledClasses = new Set(LAND_COVER_CLASSES.map(c => c.value))
    this.refreshTiles()
  }

  disableAllClasses(): void {
    this._enabledClasses.clear()
    this.refreshTiles()
  }

  /** Force MapLibre to re-fetch all tiles by updating the source URL */
  private refreshTiles(): void {
    if (!this.map || !this._enabled) {
      return
    }
    this.filterVersion++

    // Remove and re-add source+layer to force re-fetch
    const hadLayer = !!this.map.getLayer(LAYER_ID)
    if (hadLayer) {
      this.map.removeLayer(LAYER_ID)
    }
    if (this.map.getSource(SOURCE_ID)) {
      this.map.removeSource(SOURCE_ID)
    }

    this.map.addSource(SOURCE_ID, {
      type: 'raster',
      tiles: [`${PROTOCOL}://{z}/{x}/{y}?f=${this.filterVersion}`],
      tileSize: TILE_SIZE,
      minzoom: MIN_ZOOM,
      maxzoom: MAX_ZOOM,
      attribution: 'Land Cover: ESA WorldCover 2021',
    })

    if (hadLayer) {
      addOverlayLayer(
        this.map,
        {
          id: LAYER_ID,
          type: 'raster',
          source: SOURCE_ID,
          minzoom: MIN_ZOOM,
          paint: {
            'raster-opacity': this._opacity,
            'raster-fade-duration': 150,
          },
        },
        'land-cover'
      )
    }
  }

  // ---- COG management ----

  /**
   * Determine the COG filename for a given lat/lng.
   * ESA WorldCover tiles are 3×3 degrees, named by SW corner.
   */
  private getCogFilename(lat: number, lng: number): string {
    // Floor to nearest 3-degree grid
    const latBase = Math.floor(lat / 3) * 3
    const lngBase = Math.floor(lng / 3) * 3

    const latPrefix = latBase >= 0 ? 'N' : 'S'
    const lngPrefix = lngBase >= 0 ? 'E' : 'W'

    const latStr = String(Math.abs(latBase)).padStart(2, '0')
    const lngStr = String(Math.abs(lngBase)).padStart(3, '0')

    return `ESA_WorldCover_10m_2021_v200_${latPrefix}${latStr}${lngPrefix}${lngStr}_Map`
  }

  private async getCogEntry(filename: string): Promise<CogCacheEntry> {
    const cached = this.cogCache.get(filename)
    if (cached) {
      return cached
    }

    const inflight = this.cogLoading.get(filename)
    if (inflight) {
      return inflight
    }

    const promise = this.loadCog(filename)
    this.cogLoading.set(filename, promise)
    try {
      return await promise
    } finally {
      this.cogLoading.delete(filename)
    }
  }

  private async loadCog(filename: string): Promise<CogCacheEntry> {
    if (this.cogCache.size >= MAX_COG_CACHE) {
      const firstKey = this.cogCache.keys().next().value!
      this.cogCache.delete(firstKey)
    }

    const url = `${COG_BASE_URL}/${filename}.tif`
    const tiff = await fromUrl(url)
    const image0 = await tiff.getImage(0)
    const bbox = image0.getBoundingBox() as [number, number, number, number]
    const entry: CogCacheEntry = { tiff, images: new Map([[0, image0]]), bbox }
    this.cogCache.set(filename, entry)
    return entry
  }

  private async getImage(entry: CogCacheEntry, overviewIndex: number): Promise<GeoTIFFImage> {
    const cached = entry.images.get(overviewIndex)
    if (cached) {
      return cached
    }

    const image = await entry.tiff.getImage(overviewIndex)
    entry.images.set(overviewIndex, image)
    return image
  }

  /**
   * Map MapLibre zoom level to COG image index.
   * ESA WorldCover COGs have 7 images: index 0 is native 10m (36000×36000),
   * each subsequent index is a 2x reduction.
   */
  private zoomToOverviewIndex(z: number): number {
    if (z >= 13) return 0 // 36000×36000 — 10m
    if (z >= 11) return 1 // 18000×18000 — 20m
    if (z >= 9) return 2 // 9000×9000  — 40m
    if (z >= 7) return 3 // 4500×4500  — 80m
    return 4 // 2250×2250  — 160m
  }

  // ---- Tile coordinate math ----

  /** Convert Web Mercator tile z/x/y to EPSG:4326 bounding box */
  private tileBoundsToLatLng(
    z: number,
    x: number,
    y: number
  ): { west: number; south: number; east: number; north: number } {
    const n = Math.pow(2, z)
    const west = (x / n) * 360 - 180
    const east = ((x + 1) / n) * 360 - 180
    const north = (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) * 180) / Math.PI
    const south = (Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 1)) / n))) * 180) / Math.PI
    return { west, south, east, north }
  }

  // ---- Multi-COG tile compositing ----

  private async readTilePixels(
    bounds: { west: number; south: number; east: number; north: number },
    overviewIndex: number,
    signal: AbortSignal
  ): Promise<Uint8Array> {
    const tileWidth = bounds.east - bounds.west
    const tileHeight = bounds.north - bounds.south
    const output = new Uint8Array(TILE_SIZE * TILE_SIZE)

    const latMin = Math.floor(bounds.south / 3) * 3
    const latMax = Math.floor(bounds.north / 3) * 3
    const lngMin = Math.floor(bounds.west / 3) * 3
    const lngMax = Math.floor(bounds.east / 3) * 3

    const cogReads: Promise<void>[] = []

    for (let latBase = latMin; latBase <= latMax; latBase += 3) {
      for (let lngBase = lngMin; lngBase <= lngMax; lngBase += 3) {
        cogReads.push(
          this.readCogContribution(
            latBase,
            lngBase,
            bounds,
            tileWidth,
            tileHeight,
            overviewIndex,
            output,
            signal
          )
        )
      }
    }

    await Promise.all(cogReads)
    return output
  }

  private async readCogContribution(
    latBase: number,
    lngBase: number,
    bounds: { west: number; south: number; east: number; north: number },
    tileWidth: number,
    tileHeight: number,
    overviewIndex: number,
    output: Uint8Array,
    signal: AbortSignal
  ): Promise<void> {
    if (isAborted(signal)) return

    const cogWest = lngBase
    const cogEast = lngBase + 3
    const cogSouth = latBase
    const cogNorth = latBase + 3

    const overlapWest = Math.max(bounds.west, cogWest)
    const overlapEast = Math.min(bounds.east, cogEast)
    const overlapSouth = Math.max(bounds.south, cogSouth)
    const overlapNorth = Math.min(bounds.north, cogNorth)

    if (overlapWest >= overlapEast || overlapSouth >= overlapNorth) return

    const filename = this.getCogFilename(latBase + 1.5, lngBase + 1.5)
    let entry: CogCacheEntry
    try {
      entry = await this.getCogEntry(filename)
    } catch {
      return
    }

    if (isAborted(signal)) return

    const image = await this.getImage(entry, overviewIndex)
    const bbox = entry.bbox
    const imgWidth = image.getWidth()
    const imgHeight = image.getHeight()

    const pxPerDegX = imgWidth / (bbox[2] - bbox[0])
    const pxPerDegY = imgHeight / (bbox[3] - bbox[1])

    const winLeft = Math.max(0, Math.floor((overlapWest - bbox[0]) * pxPerDegX))
    const winRight = Math.min(imgWidth, Math.ceil((overlapEast - bbox[0]) * pxPerDegX))
    const winTop = Math.max(0, Math.floor((bbox[3] - overlapNorth) * pxPerDegY))
    const winBottom = Math.min(imgHeight, Math.ceil((bbox[3] - overlapSouth) * pxPerDegY))

    if (winRight <= winLeft || winBottom <= winTop) return

    const outLeft = Math.round(((overlapWest - bounds.west) / tileWidth) * TILE_SIZE)
    const outRight = Math.round(((overlapEast - bounds.west) / tileWidth) * TILE_SIZE)
    const outTop = Math.round(((bounds.north - overlapNorth) / tileHeight) * TILE_SIZE)
    const outBottom = Math.round(((bounds.north - overlapSouth) / tileHeight) * TILE_SIZE)

    const outW = Math.max(1, outRight - outLeft)
    const outH = Math.max(1, outBottom - outTop)

    const rasters = await image.readRasters({
      window: [winLeft, winTop, winRight, winBottom],
      width: outW,
      height: outH,
      interleave: false,
    })

    if (isAborted(signal)) return

    const pixels = new Uint8Array(rasters[0] as unknown as ArrayBuffer)
    for (let row = 0; row < outH; row++) {
      const destRow = outTop + row
      if (destRow < 0 || destRow >= TILE_SIZE) continue
      for (let col = 0; col < outW; col++) {
        const destCol = outLeft + col
        if (destCol < 0 || destCol >= TILE_SIZE) continue
        output[destRow * TILE_SIZE + destCol] = pixels[row * outW + col]
      }
    }
  }

  // ---- Tile request handler ----

  private async handleTileRequest(
    params: { url: string },
    abortController: AbortController
  ): Promise<{ data: ArrayBuffer }> {
    const match = params.url.match(/(\d+)\/(\d+)\/(\d+)/)
    if (!match) {
      throw new Error(`Invalid land cover tile URL: ${params.url}`)
    }

    const z = parseInt(match[1])
    const x = parseInt(match[2])
    const y = parseInt(match[3])
    const key = `${z}/${x}/${y}`

    if (z < MIN_ZOOM) {
      return { data: this.transparentTile() }
    }

    this.pendingRequests.set(key, abortController)

    try {
      const bounds = this.tileBoundsToLatLng(z, x, y)
      const overviewIndex = this.zoomToOverviewIndex(z)

      if (isAborted(abortController.signal)) {
        throw new DOMException('Aborted', 'AbortError')
      }

      const classificationPixels = await this.readTilePixels(
        bounds,
        overviewIndex,
        abortController.signal
      )

      // Send to worker for coloring
      const coloredPixels = await this.processInWorker(
        classificationPixels,
        Array.from(this._enabledClasses),
        abortController.signal
      )

      // Encode to PNG
      const canvas = new OffscreenCanvas(TILE_SIZE, TILE_SIZE)
      const ctx = canvas.getContext('2d')!
      const imageData = new ImageData(
        new Uint8ClampedArray(coloredPixels.buffer as ArrayBuffer),
        TILE_SIZE,
        TILE_SIZE
      )
      ctx.putImageData(imageData, 0, 0)

      const blob = await canvas.convertToBlob({ type: 'image/png' })
      const arrayBuffer = await blob.arrayBuffer()

      return { data: arrayBuffer }
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        throw e
      }
      // Return transparent tile on error (e.g., COG not available for this area)
      log.warn(`Land cover tile error for ${key}:`, e instanceof Error ? e.message : String(e))
      return { data: this.transparentTile() }
    } finally {
      this.pendingRequests.delete(key)
    }
  }

  private processInWorker(
    classificationPixels: Uint8Array,
    enabledClasses: number[],
    signal: AbortSignal
  ): Promise<Uint8ClampedArray> {
    return new Promise((resolve, reject) => {
      if (signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'))
        return
      }

      const requestId = this.nextRequestId++
      const worker = this.getWorker()
      const handler = (e: MessageEvent): void => {
        if (e.data.requestId !== requestId) {
          return
        }
        worker.removeEventListener('message', handler)
        signal.removeEventListener('abort', onAbort)
        resolve(new Uint8ClampedArray(e.data.pixels))
      }
      const onAbort = (): void => {
        worker.removeEventListener('message', handler)
        reject(new DOMException('Aborted', 'AbortError'))
      }

      worker.addEventListener('message', handler)
      signal.addEventListener('abort', onAbort)
      worker.postMessage({ classificationPixels, enabledClasses, requestId }, [
        classificationPixels.buffer,
      ])
    })
  }

  /** Generate a 1x1 transparent PNG for empty/error tiles */
  private transparentTile(): ArrayBuffer {
    // Minimal 1x1 transparent PNG
    const png = new Uint8Array([
      0x89,
      0x50,
      0x4e,
      0x47,
      0x0d,
      0x0a,
      0x1a,
      0x0a, // PNG signature
      0x00,
      0x00,
      0x00,
      0x0d,
      0x49,
      0x48,
      0x44,
      0x52, // IHDR chunk
      0x00,
      0x00,
      0x00,
      0x01,
      0x00,
      0x00,
      0x00,
      0x01,
      0x08,
      0x06,
      0x00,
      0x00,
      0x00,
      0x1f,
      0x15,
      0xc4,
      0x89,
      0x00,
      0x00,
      0x00,
      0x0a,
      0x49,
      0x44,
      0x41, // IDAT chunk
      0x54,
      0x78,
      0x9c,
      0x62,
      0x00,
      0x00,
      0x00,
      0x02,
      0x00,
      0x01,
      0xe5,
      0x27,
      0xde,
      0xfc,
      0x00,
      0x00, // IEND chunk
      0x00,
      0x00,
      0x49,
      0x45,
      0x4e,
      0x44,
      0xae,
      0x42,
      0x60,
      0x82,
    ])
    return png.buffer
  }

  // ---- Point query API (for future track profiling) ----

  async queryClassesInBbox(
    bounds: [number, number, number, number], // [west, south, east, north]
    outputWidth: number,
    outputHeight: number
  ): Promise<Uint8Array> {
    const [west, south, east, north] = bounds
    const output = new Uint8Array(outputWidth * outputHeight)
    const bboxWidth = east - west
    const bboxHeight = north - south

    // overview index 2 ≈ 40m — closest to z12 DEM grid resolution
    const overviewIndex = 2

    const latMin = Math.floor(south / 3) * 3
    const latMax = Math.floor(north / 3) * 3
    const lngMin = Math.floor(west / 3) * 3
    const lngMax = Math.floor(east / 3) * 3

    const reads: Promise<void>[] = []

    for (let latBase = latMin; latBase <= latMax; latBase += 3) {
      for (let lngBase = lngMin; lngBase <= lngMax; lngBase += 3) {
        reads.push(
          (async () => {
            const cogWest = lngBase
            const cogEast = lngBase + 3
            const cogSouth = latBase
            const cogNorth = latBase + 3

            const overlapWest = Math.max(west, cogWest)
            const overlapEast = Math.min(east, cogEast)
            const overlapSouth = Math.max(south, cogSouth)
            const overlapNorth = Math.min(north, cogNorth)
            if (overlapWest >= overlapEast || overlapSouth >= overlapNorth) return

            const filename = this.getCogFilename(latBase + 1.5, lngBase + 1.5)
            let entry: CogCacheEntry
            try {
              entry = await this.getCogEntry(filename)
            } catch {
              return
            }

            const image = await this.getImage(entry, overviewIndex)
            const cogBbox = entry.bbox
            const imgWidth = image.getWidth()
            const imgHeight = image.getHeight()

            const pxPerDegX = imgWidth / (cogBbox[2] - cogBbox[0])
            const pxPerDegY = imgHeight / (cogBbox[3] - cogBbox[1])

            const winLeft = Math.max(0, Math.floor((overlapWest - cogBbox[0]) * pxPerDegX))
            const winRight = Math.min(imgWidth, Math.ceil((overlapEast - cogBbox[0]) * pxPerDegX))
            const winTop = Math.max(0, Math.floor((cogBbox[3] - overlapNorth) * pxPerDegY))
            const winBottom = Math.min(
              imgHeight,
              Math.ceil((cogBbox[3] - overlapSouth) * pxPerDegY)
            )
            if (winRight <= winLeft || winBottom <= winTop) return

            const outLeft = Math.round(((overlapWest - west) / bboxWidth) * outputWidth)
            const outRight = Math.round(((overlapEast - west) / bboxWidth) * outputWidth)
            const outTop = Math.round(((north - overlapNorth) / bboxHeight) * outputHeight)
            const outBottom = Math.round(((north - overlapSouth) / bboxHeight) * outputHeight)
            const outW = Math.max(1, outRight - outLeft)
            const outH = Math.max(1, outBottom - outTop)

            const rasters = await image.readRasters({
              window: [winLeft, winTop, winRight, winBottom],
              width: outW,
              height: outH,
              interleave: false,
            })

            const pixels = new Uint8Array(rasters[0] as unknown as ArrayBuffer)
            for (let row = 0; row < outH; row++) {
              const destRow = outTop + row
              if (destRow < 0 || destRow >= outputHeight) continue
              for (let col = 0; col < outW; col++) {
                const destCol = outLeft + col
                if (destCol < 0 || destCol >= outputWidth) continue
                output[destRow * outputWidth + destCol] = pixels[row * outW + col]
              }
            }
          })()
        )
      }
    }

    await Promise.all(reads)
    return output
  }

  async queryClassAtPoint(lat: number, lng: number): Promise<number | null> {
    try {
      const cogFilename = this.getCogFilename(lat, lng)
      const entry = await this.getCogEntry(cogFilename)
      const image = await this.getImage(entry, 0) // native resolution

      const bbox = entry.bbox
      const imgWidth = image.getWidth()
      const imgHeight = image.getHeight()

      const px = Math.floor(((lng - bbox[0]) / (bbox[2] - bbox[0])) * imgWidth)
      const py = Math.floor(((bbox[3] - lat) / (bbox[3] - bbox[1])) * imgHeight)

      if (px < 0 || px >= imgWidth || py < 0 || py >= imgHeight) {
        return null
      }

      const rasters = await image.readRasters({
        window: [px, py, px + 1, py + 1],
        width: 1,
        height: 1,
        interleave: false,
      })

      return (rasters[0] as unknown as number[])[0]
    } catch {
      return null
    }
  }
}

export const landCoverService = new LandCoverService()
